<template>
  <div class="collection-main" :class="`${createRenderType}-collection-main`">
    <div v-if="!loading" class="genre-box genre-total">
      <div class="genre-title flex-space-between">
        <span>{{ $$t("credit.moreGenres") }}</span>
        <div v-if="createRenderType == 'pc'" class="change-btn flex">
          <div
            class="btn-item flex-center"
            :class="showData.first ? 'btn-item-disabled' : 'pointer'"
            @click="changePrev"
          >
            <img src="@/assets/img/content/prev.png" alt="" />
          </div>
          <div
            class="btn-item flex-center"
            @click="changeNext"
            :class="showData.last ? 'btn-item-disabled' : 'pointer'"
          >
            <img src="@/assets/img/content/next.png" alt="" />
          </div>
        </div>
      </div>
      <div class="total" ref="refGenreSwiperBox">
        <div class="swiper" ref="swiperContainer">
          <div class="swiper-wrapper">
            <div
              v-for="item in genreCollection"
              :key="item?.image?.id"
              class="swiper-slide item pointer"
              @click="handleGoGenreDetail(item?.image?.id, item?.image?.title)"
            >
              <LazyGenreImg
                :cover="
                  createRenderType == 'pc'
                    ? JSON.parse(item?.image?.cover)?.cover_pc
                    : JSON.parse(item?.image?.cover)?.cover_h5
                "
              ></LazyGenreImg>
              <div class="name">
                {{
                  constTags
                    .getRenderLabel(
                      [item?.image?.title],
                      constTags.StyleLabelData.map,
                      $$language()
                    )
                    ?.join("") || item?.image?.title
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <el-skeleton
        class="skeleton-box"
        :class="`${createRenderType}-skeleton-box`"
        :rows="6"
        :style="{
          '--el-fill-color': 'rgb(255, 255, 255, 0.1)',
        }"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, getCurrentInstance } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import SwiperCore from "swiper";
import "swiper/css";
import constTags from "@/config/const.js";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
import useDiscoverStore from "@/store/discover/discoverDataStore.js";
import LazyGenreImg from "./lazyGenreImg.vue";

const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties;
const { $$t, $$language } = globalProperties?.$i18n;
const router = useRouter();
const createLayoutStore = useCreateLayoutStore();
const discoverDataStore = useDiscoverStore();
const { createRenderType } = storeToRefs(createLayoutStore);
const { genreCollection } = storeToRefs(discoverDataStore);

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
});
const swiperContainer = ref(null);
const refGenreSwiperBox = ref(null);
const swiperInstance = ref(null);
const swiperActiveIndex = ref(0);
const showData = ref({
  first: false,
  last: false,
});

const observerObj = {
  first: null,
  last: null,
};

const handleGoGenreDetail = (id, name, type) => {
  globalProperties.logClickEvent("home_genres_cover", {
    entity: "home_genres_cover",
  });
  discoverDataStore.clearGenreListTotal();
  router.push({
    name: "GenreDetail",
    params: {
      id,
      name,
    },
  });
};

const initEvent = () => {
  nextTick(() => {
    if (refGenreSwiperBox.value && genreCollection.value.length) {
      setTimeout(() => {
        let els = refGenreSwiperBox.value
          ?.querySelector(".swiper-wrapper")
          ?.querySelectorAll(".swiper-slide");
        if (els && els.length > 1) {
          let elf = els[0];
          let elr = els[genreCollection.value.length - 1];
          isElementHalfVisible(elf, "first");
          isElementHalfVisible(elr, "last");
        }
      }, 10);
    }
  });
};

const isElementHalfVisible = (element, type) => {
  observerObj[type] = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (type === "first") {
          // 第一个元素完全可见时才禁用"上一个"按钮
          // showData.value.first = entry.isIntersecting;
          showData.value.first =
            entry.isIntersecting && entry.intersectionRatio >= 0.95;
        } else {
          showData.value.last =
            entry.isIntersecting && entry.intersectionRatio >= 0.95;
        }
      });
    },
    {
      root: refGenreSwiperBox.value,
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
    }
  );

  observerObj[type].observe(element);
};
const onSlideChange = (swiper) => {
  swiperActiveIndex.value = swiper.activeIndex;
};

const changePrev = () => {
  swiperInstance.value?.slideTo(swiperActiveIndex.value - 3);
};

const changeNext = () => {
  swiperInstance.value?.slideTo(swiperActiveIndex.value + 3);
};

const initSwiper = () => {
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true);
    swiperInstance.value = null;
  }
  swiperInstance.value = new SwiperCore(swiperContainer.value, {
    slidesPerView: "auto",
    spaceBetween: 20,
    simulateTouch: true,
    allowTouchMove: true,
    grabCursor: true,
    on: {
      slideChange: onSlideChange,
    },
  });
};

watch(
  () => props.loading,
  (newVal) => {
    if (!newVal) {
      nextTick(() => {
        initSwiper();
        initEvent();
      });
    }
  }
);
</script>

<style scoped>
.swiper {
  width: 100%;
  overflow: hidden;
  cursor: grab;
}
.swiper:active {
  cursor: grabbing;
}
.swiper-wrapper {
  display: flex;
}
.swiper-slide {
  flex-shrink: 0;
  /* width: 200px;
  height: 120px; */
  width: max-content;
  height: max-content;
  /* background: #ccc; */
  margin-right: 10px;
}
@keyframes opacityAnimation {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}
.skeleton-box {
  margin-top: 64px;
  animation: opacityAnimation 1.8s infinite;
}
.pc-skeleton-box {
  padding-right: 20px;
}
.mobile-skeleton-box {
  padding: 0 16px;
}
</style>

<style lang="scss" scoped>
.pc-collection-main {
  .genre-title {
    padding-right: 20px;
    font-size: 32px;
    font-weight: bold;
    line-height: 42px;
    span {
      &:first-of-type {
        text-transform: capitalize;
      }
    }
    .change-btn {
      .btn-item {
        width: 40px;
        height: 40px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
        margin-left: 12px;
        &-disabled {
          opacity: 0.3;
        }
        img {
          width: 20px;
          height: 20px;
        }
      }
      .pointer {
        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }
    .more {
      font-size: 16px;
      font-weight: 500;
      line-height: 36px;
      border-radius: 30px;
      padding: 0 8px 0 16px;
      &::after {
        content: "";
        width: 16px;
        height: 16px;
        background: url("@/assets/img/content/next.png");
        background-size: 100% 100%;
        margin-left: 2px;
      }
      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }
  .total {
    overflow-x: hidden;
    margin-top: 24px;
    .item {
      position: relative;
      border-radius: 16px;
      overflow: hidden;
      img {
        width: 220px;
        height: 128px;
        display: block;
        transition: transform 0.3s ease-in-out;
        transform-origin: center;
      }
      .name {
        position: absolute;
        text-transform: capitalize;
        left: 12px;
        bottom: 12px;
        padding: 8px 16px;
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(10px);
        font-size: 16px;
      }
      &:hover {
        img {
          transform: scale(1.075);
        }
      }
    }
  }
}

.mobile-collection-main {
  .genre-box {
    margin-top: 20px;
    padding-left: 16px;
    .genre-title {
      font-size: 24px;
      font-weight: bold;
      line-height: 32px;
      span {
        &:first-of-type {
          text-transform: capitalize;
        }
      }
      .more {
        font-size: 14px;
        line-height: 20px;
      }
    }
  }
  .total {
    overflow-x: hidden;
    margin-top: 16px;
    .item {
      position: relative;
      .name {
        position: absolute;
        left: 8px;
        bottom: 8px;
        padding: 7px 10px;
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(10px);
        font-size: 14px;
        text-transform: capitalize;
      }
      img {
        width: 160px;
        height: 93px;
        border-radius: 12px;
        display: block;
      }
    }
  }
}
</style>
